import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, Upload, PackageCheck, Terminal, CheckCircle2, AlertCircle, Wifi } from "lucide-react";
import { Section, Field, Header } from "./TextsTab";

export default function DeployTab() {
  const [cfg, setCfg] = useState({ host: "", user: "", password: "", remote_path: "/", port: 21 });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [building, setBuilding]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const [log, setLog] = useState([]);

  useEffect(() => {
    api.get("/admin/ftp-config").then(r => setCfg(c => ({ ...c, ...r.data }))).catch(() => {});
  }, []);

  const addLog = (msg, type = "info") => setLog(l => [...l, { msg, type, ts: new Date().toLocaleTimeString() }]);

  const testFTP = async () => {
    setTesting(true);
    addLog("Probando conexión FTP…");
    try {
      const { data } = await api.post("/admin/deploy/test-ftp");
      addLog(data.message, "ok");
      if (data.server) addLog(`Servidor: ${data.server}`, "info");
      toast.success("Conexión FTP OK");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Error de conexión";
      addLog(msg, "error");
      toast.error(msg);
    } finally { setTesting(false); }
  };

  const saveCfg = async () => {
    setSaving(true);
    try {
      await api.post("/admin/ftp-config", cfg);
      toast.success("Configuración FTP guardada");
      addLog("Configuración FTP guardada", "ok");
    } catch { toast.error("Error al guardar config"); }
    finally { setSaving(false); }
  };

  const exportContent = async () => {
    setExporting(true);
    addLog("Exportando contenido de la base de datos…");
    try {
      const { data } = await api.post("/admin/deploy/export");
      addLog(`Contenido exportado a frontend/public/data/content.json (${data.exported_at})`, "ok");
      toast.success("Contenido exportado");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Error al exportar";
      addLog(msg, "error");
      toast.error(msg);
    } finally { setExporting(false); }
  };

  const buildFrontend = async () => {
    setBuilding(true);
    addLog("Iniciando compilación React… (puede tardar 2-3 min)");
    try {
      const { data } = await api.post("/admin/deploy/build", {}, { timeout: 600000 });
      addLog(data.message, "ok");
      toast.success("Build completado");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Error al compilar";
      addLog(msg.slice(0, 400), "error");
      toast.error("Build falló");
    } finally { setBuilding(false); }
  };

  const uploadFTP = async () => {
    setUploading(true);
    addLog("Conectando al FTP de DonWeb…");
    try {
      const { data } = await api.post("/admin/deploy/upload");
      addLog(`Deploy completado — ${data.files_uploaded} archivos subidos`, "ok");
      toast.success(`Deploy OK — ${data.files_uploaded} archivos`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Error en el deploy";
      addLog(msg, "error");
      toast.error(msg);
    } finally { setUploading(false); }
  };

  return (
    <div className="space-y-8 max-w-3xl">
      <Header title="Deploy a DonWeb" subtitle="Publicá la web en tu hosting con un clic. El contenido se exporta como archivos estáticos." />

      {/* FTP Config */}
      <Section title="Configuración FTP">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Host FTP (ej: ftp.tudominio.com)" value={cfg.host} onChange={v => setCfg(c => ({ ...c, host: v }))} />
          <Field label="Puerto" value={String(cfg.port)} onChange={v => setCfg(c => ({ ...c, port: Number(v) || 21 }))} />
        </div>
        <Field label="Usuario FTP" value={cfg.user} onChange={v => setCfg(c => ({ ...c, user: v }))} />
        <div>
          <label className="block text-xs text-[#9bb0e0] mb-1.5">Contraseña</label>
          <input
            type="password"
            value={cfg.password}
            onChange={e => setCfg(c => ({ ...c, password: e.target.value }))}
            className="w-full bg-[rgba(10,20,55,0.5)] border border-[rgba(120,160,255,0.18)] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#5fa0ff]"
          />
        </div>
        <Field label="Ruta remota (ej: /public_html)" value={cfg.remote_path} onChange={v => setCfg(c => ({ ...c, remote_path: v }))} />
        <div className="flex gap-3 flex-wrap">
          <button onClick={saveCfg} disabled={saving} className="btn-primary px-5 py-2.5 rounded-lg inline-flex items-center gap-2 text-sm font-medium">
            <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar configuración"}
          </button>
          <button onClick={testFTP} disabled={testing || !cfg.host} className="btn-line px-5 py-2.5 rounded-lg inline-flex items-center gap-2 text-sm font-medium disabled:opacity-50">
            <Wifi className="w-4 h-4" /> {testing ? "Probando…" : "Probar conexión"}
          </button>
        </div>
      </Section>

      {/* Deploy steps */}
      <Section title="Proceso de deploy">
        <div className="space-y-5">
          {/* Step 1 */}
          <div className="flex gap-4 items-start">
            <div className="w-8 h-8 rounded-full bg-brand flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5">1</div>
            <div className="flex-1">
              <div className="text-fg font-medium text-sm mb-1">Exportar contenido</div>
              <p className="text-mute text-xs leading-relaxed mb-3">Genera el archivo <code className="text-[#7da3ff]">frontend/public/data/content.json</code> con todo el contenido actual (textos, servicios, clientes, fotos).</p>
              <button onClick={exportContent} disabled={exporting} className="btn-primary px-4 py-2 rounded-lg inline-flex items-center gap-2 text-sm">
                <PackageCheck className="w-4 h-4" /> {exporting ? "Exportando…" : "Exportar contenido"}
              </button>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex gap-4 items-start">
            <div className="w-8 h-8 rounded-full bg-brand flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5">2</div>
            <div className="flex-1">
              <div className="text-fg font-medium text-sm mb-1">Compilar React</div>
              <p className="text-mute text-xs leading-relaxed mb-3">
                Genera la carpeta <code className="text-[#7da3ff]">frontend/build/</code> con la web lista para producción.
              </p>
              <button onClick={buildFrontend} disabled={building} className="btn-primary px-4 py-2 rounded-lg inline-flex items-center gap-2 text-sm mb-1 disabled:opacity-50">
                <Terminal className="w-4 h-4" /> {building ? "Compilando… (2-3 min)" : "Compilar"}
              </button>
            </div>
          </div>

          {/* Step 3 */}
          <div className="flex gap-4 items-start">
            <div className="w-8 h-8 rounded-full bg-brand flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5">3</div>
            <div className="flex-1">
              <div className="text-fg font-medium text-sm mb-1">Subir a DonWeb</div>
              <p className="text-mute text-xs leading-relaxed mb-3">Sube el build + imágenes/logos al FTP. La web en DonWeb queda actualizada.</p>
              <button onClick={uploadFTP} disabled={uploading || !cfg.host} className="btn-solid px-4 py-2 rounded-lg inline-flex items-center gap-2 text-sm disabled:opacity-50">
                <Upload className="w-4 h-4" /> {uploading ? "Subiendo archivos…" : "Subir a DonWeb"}
              </button>
              {!cfg.host && <p className="text-[#7da3ff] text-[11px] mt-2">Configurá el FTP primero.</p>}
            </div>
          </div>
        </div>
      </Section>

      {/* Log */}
      {log.length > 0 && (
        <Section title="Log">
          <div className="bg-[#060A14] border border-line rounded-lg p-4 space-y-1.5 max-h-48 overflow-y-auto">
            {log.map((entry, i) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                {entry.type === "ok"    && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />}
                {entry.type === "error" && <AlertCircle  className="w-3.5 h-3.5 text-red-400    flex-shrink-0 mt-0.5" />}
                {entry.type === "info"  && <span className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-[#7da3ff]">·</span>}
                <span className={entry.type === "error" ? "text-red-300" : entry.type === "ok" ? "text-emerald-300" : "text-[#9bb0e0]"}>
                  <span className="text-faint mr-2">{entry.ts}</span>{entry.msg}
                </span>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
